import React from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { touchHitArea } from "../../theme";

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  leading?: React.ReactNode;
  /** Breadcrumb parent; renders "Parent ›" above the title. */
  parent?: { label: string; onClick: () => void };
  /** Right-aligned controls (live state, primary action). Hidden on phones. */
  actions?: React.ReactNode;
  /** Phones only: compact line under the subtitle (e.g. live status). */
  mobileMeta?: React.ReactNode;
  /** Phones only: compact action next to the title (e.g. a 44px "+" button). */
  mobileAction?: React.ReactNode;
}

/** Page title block: optional breadcrumb + leading avatar, title, subtitle, actions. */
export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, leading, parent, actions, mobileMeta, mobileAction }) => (
  <Stack
    direction="row"
    alignItems="flex-end"
    justifyContent="space-between"
    spacing={2}
    sx={{ pt: { xs: 1, md: 3 }, mb: { xs: 2, md: 3 }, minWidth: 0 }}
  >
    <Stack direction="row" alignItems="center" spacing={1.75} sx={{ minWidth: 0 }}>
      {leading}
      <Box sx={{ minWidth: 0 }}>
        {parent && (
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 0.25 }}>
            <Link
              component="button"
              onClick={parent.onClick}
              sx={{
                position: "relative",
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "text.secondary",
                "&:hover": { color: "text.primary" },
                ...touchHitArea,
              }}
            >
              {parent.label}
            </Link>
            <ChevronRightRoundedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
          </Stack>
        )}
        <Typography
          component="h1"
          sx={{ fontSize: { xs: "1.625rem", md: "2rem" }, fontWeight: 750, letterSpacing: "-0.03em", lineHeight: 1.1 }}
          noWrap
        >
          {title}
        </Typography>
        {subtitle &&
          (typeof subtitle === "string" ? (
            <Typography sx={{ mt: 0.5, fontSize: { xs: "0.875rem", md: "0.9375rem" }, color: "text.secondary" }} noWrap>
              {subtitle}
            </Typography>
          ) : (
            // Rich subtitles (e.g. text + a copy button) lay out as a row; the caller handles ellipsis
            // on its text so controls keep their full touch area (no clipping container).
            <Box
              sx={{
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                fontSize: { xs: "0.875rem", md: "0.9375rem" },
                color: "text.secondary",
              }}
            >
              {subtitle}
            </Box>
          ))}
        {mobileMeta && (
          <Typography component="div" sx={{ display: { xs: "block", sm: "none" }, mt: 0.75, fontSize: "0.8125rem", color: "text.secondary" }}>
            {mobileMeta}
          </Typography>
        )}
      </Box>
    </Stack>

    {actions && (
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0, display: { xs: "none", sm: "flex" } }}>
        {actions}
      </Stack>
    )}
    {mobileAction && <Box sx={{ flexShrink: 0, display: { xs: "block", sm: "none" }, alignSelf: "center" }}>{mobileAction}</Box>}
  </Stack>
);

export default PageHeader;
